import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { money } from "@/lib/format";
import { TAG_STYLES, TYPE_OPTS } from "@/lib/budget";
import { barTone, difOf, restTone, type Concept, type TrackCategory, type TrackSection } from "@/lib/tracking";

const COLS = "grid-cols-[minmax(0,1fr)_130px_130px_130px]";
const restText = (s: TrackSection, planned: number, actual: number) => money(Math.abs(difOf(s.key, planned, actual)));

function Tag({ c }: { c: Concept }) {
  if (!c.tag) return <span className="w-5" />;
  return <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ${TAG_STYLES[c.tag]}`} title={TYPE_OPTS.find((t) => t.tag === c.tag)?.label}>{c.tag}</span>;
}

function FlowLine({ flow, testId }: { flow?: { contributed: number; withdrawn: number }; testId: string }) {
  if (!flow) return null;
  return <span className="block text-xs font-normal text-slate-500" data-testid={testId}>Aportado {money(flow.contributed)} · Retirado {money(flow.withdrawn)}</span>;
}

function ConceptRow({ s, c }: { s: TrackSection; c: Concept }) {
  if (c.note) {
    return (
      <li className="flex items-center justify-between gap-3 py-1.5 pl-10 pr-4 text-xs italic text-slate-500" data-testid={`concept-row-${c.id}`}>
        <span className="truncate">Incluye: {c.name}</span><span className="tabular shrink-0">{money(c.actual)}</span>
      </li>
    );
  }
  const tone = c.unbudgeted ? "bg-[#FFFBEB] text-amber-800" : "text-slate-600";
  return (
    <li data-testid={`concept-row-${c.id}`}>
      <div className={`hidden ${COLS} items-center gap-3 py-2 pl-10 pr-4 text-sm md:grid ${tone}`}>
        <span className="flex min-w-0 items-center gap-2"><Tag c={c} /><span className="truncate">{c.name}</span></span>
        <span className="tabular text-right">{c.unbudgeted ? "—" : money(c.planned)}</span>
        <span className="tabular text-right">{money(c.actual)}</span>
        <span className={`tabular text-right ${c.unbudgeted ? "" : restTone(s.key, c.planned, c.actual)}`}>{c.unbudgeted ? "" : restText(s, c.planned, c.actual)}</span>
      </div>
      <div className={`flex items-center justify-between gap-3 py-2 pl-6 pr-4 text-sm md:hidden ${tone}`}>
        <span className="flex min-w-0 items-center gap-2"><Tag c={c} /><span className="truncate">{c.name}</span></span>
        <span className="tabular shrink-0"><b>{money(c.actual)}</b>{!c.unbudgeted && ` de ${money(c.planned)}`}</span>
      </div>
    </li>
  );
}

function CategoryRow({ s, c }: { s: TrackSection; c: TrackCategory }) {
  const [open, setOpen] = useState(false);
  const pct = c.planned > 0 ? Math.min(c.actual / c.planned, 1) * 100 : c.actual > 0 ? 100 : 0;
  return (
    <li className="border-t border-[#DDE1E8]">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left hover:bg-slate-50" aria-expanded={open} data-testid={`track-category-${c.id}`}>
        <div className={`hidden ${COLS} items-center gap-3 px-4 py-3 md:grid`}>
          <span className="flex items-center gap-2 font-semibold text-[#1B2A4A]"><ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} /><span>{c.name}<FlowLine flow={c.flow} testId={`track-flow-${c.id}`} /></span></span>
          <span className="tabular text-right text-slate-600">{money(c.planned)}</span>
          <span className="tabular text-right font-semibold text-[#1B2A4A]">{money(c.actual)}</span>
          <span className={`tabular text-right font-semibold ${restTone(s.key, c.planned, c.actual)}`} data-testid={`track-rest-${c.id}`}>{restText(s, c.planned, c.actual)}</span>
        </div>
        <div className="px-4 py-3 md:hidden" data-testid={`track-card-${c.id}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 font-semibold text-[#1B2A4A]"><ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} /><span className="truncate">{c.name}</span></span>
            <span className="tabular shrink-0 text-sm text-slate-600"><b className="text-[#1B2A4A]">{money(c.actual)}</b> de {money(c.planned)}</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#EEF0F3]"><div className={`h-full rounded-full ${barTone(s.key, c.planned, c.actual)}`} style={{ width: `${pct}%` }} /></div>
        </div>
      </button>
      {open && (
        <ul className="pb-2" data-testid={`track-concepts-${c.id}`}>
          {c.concepts.length ? c.concepts.map((k) => <ConceptRow key={k.id} s={s} c={k} />) : <li className="py-2 pl-10 text-sm text-slate-400">Sin conceptos</li>}
        </ul>
      )}
    </li>
  );
}

function TotalRow({ s }: { s: TrackSection }) {
  return (
    <div className="border-t-2 border-[#16294D] bg-[#F4F5F7]" data-testid={`track-total-row-${s.key}`}>
      <div className={`hidden ${COLS} items-center gap-3 px-4 py-3 md:grid`}>
        <span className="font-bold text-[#16294D]">Total {s.title.toLowerCase()}</span>
        <span className="tabular text-right font-bold text-[#16294D]" data-testid={`track-total-planned-${s.key}`}>{money(s.planned)}</span>
        <span className="tabular text-right font-bold text-[#16294D]">{money(s.actual)}</span>
        <span className={`tabular text-right font-bold ${restTone(s.key, s.planned, s.actual)}`}>{restText(s, s.planned, s.actual)}</span>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3 md:hidden">
        <span className="font-bold text-[#16294D]">Total {s.title.toLowerCase()}</span>
        <span className="tabular text-sm text-slate-600"><b className="text-[#16294D]">{money(s.actual)}</b> de <b className="text-[#16294D]" data-testid={`track-total-planned-m-${s.key}`}>{money(s.planned)}</b></span>
      </div>
    </div>
  );
}

export function TrackingSection({ s }: { s: TrackSection }) {
  return (
    <div className="overflow-hidden rounded-[14px] border border-[#DDE1E8] bg-white" data-testid={`track-section-${s.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-4">
        <h2 className="font-display text-lg font-bold text-[#1B2A4A]">{s.title}</h2>
        <p className="tabular text-right text-sm text-slate-500" data-testid={`track-section-total-${s.key}`}>{s.realLabel} <b className="text-[#1B2A4A]">{money(s.actual)}</b> de {money(s.planned)}<FlowLine flow={s.flow} testId="track-section-flow" /></p>
      </div>
      <div className={`hidden ${COLS} gap-3 bg-[#F4F5F7] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 md:grid`}>
        <span>Categoría</span><span className="text-right">Presupuestado</span><span className="text-right">{s.realLabel}</span><span className="text-right">{s.restLabel}</span>
      </div>
      {s.cats.length ? <ul>{s.cats.map((c) => <CategoryRow key={c.id} s={s} c={c} />)}</ul>
        : <p className="border-t border-[#DDE1E8] px-4 py-4 text-sm text-slate-400">Nada presupuestado ni registrado este mes.</p>}
      {s.key === "expense" && s.cats.length > 0 && <TotalRow s={s} />}
    </div>
  );
}
